/**
 * CIPP 多语言图片资源解析
 *
 * 具体图片路径由当前语言包的 `assets` 节点提供；远程配置异常时回退到同语言 bundled 配置。
 *
 * @module locales/assets
 */

import { useCallback } from 'react';

import { getStaticAssetUrl } from '@/env';
import { useTranslation } from '@/locales/i18n';

import enUS from './en.json';
import zhCN from './zh.json';

type LocaleAssetTree = Record<string, unknown>;

const BUNDLED_ASSETS: Record<'zh-CN' | 'en-US', LocaleAssetTree> = {
  'zh-CN': zhCN.assets,
  'en-US': enUS.assets,
};

/** 将运行时语言码归一为 CIPP bundled 语言码。 */
function normalizeAssetLanguage(language?: string): 'zh-CN' | 'en-US' {
  return language?.toLowerCase().startsWith('en') ? 'en-US' : 'zh-CN';
}

/** 按点路径读取 bundled 图片配置。 */
function getBundledAssetReference(language: string | undefined, key: string): string {
  let current: unknown = BUNDLED_ASSETS[normalizeAssetLanguage(language)];
  for (const segment of key.split('.')) {
    if (!current || typeof current !== 'object' || Array.isArray(current)) return '';
    current = (current as Record<string, unknown>)[segment];
  }
  return typeof current === 'string' ? current.trim() : '';
}

/** 判断平台下发值是否是允许的图片资源引用。 */
export function isValidLocalizedAssetReference(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const normalized = value.trim();
  if (!normalized) return false;
  if (/^https?:\/\//i.test(normalized)) return true;
  if (/^[a-z][a-z\d+.-]*:/i.test(normalized)) return false;
  return normalized.startsWith('image/') && !normalized.split('/').includes('..');
}

/**
 * 解析图片资源引用，远程值无效时使用 bundled 回退。
 *
 * @param value - 当前 i18n 解析出的配置值
 * @param fallback - 同语言 bundled 配置值
 * @returns 可直接用于 `src` 的图片 URL；两者都无效时返回空串
 */
export function resolveLocalizedAssetReference(value: unknown, fallback: unknown): string {
  let reference = '';
  if (isValidLocalizedAssetReference(value)) {
    reference = value.trim();
  } else if (isValidLocalizedAssetReference(fallback)) {
    reference = fallback.trim();
  }
  if (!reference) return '';
  return /^https?:\/\//i.test(reference) ? reference : getStaticAssetUrl(reference);
}

/**
 * 获取随当前语言更新的图片 URL 解析器。
 *
 * @returns 接收 `assets` 下相对 key 的解析函数
 */
export function useLocalizedAssetResolver(): (key: string) => string {
  const { t, i18n } = useTranslation();
  const language = i18n?.resolvedLanguage ?? i18n?.language;

  return useCallback((key: string) => {
    const value = t(`assets.${key}`, { defaultValue: '' });
    const fallback = getBundledAssetReference(language, key);
    return resolveLocalizedAssetReference(value, fallback);
  }, [language, t]);
}
